import { LandingLink } from "@/modules/notifications/components/notification";
import { Notification } from "creso-core/api";
import {
  NOTIFICATION_DESCRIPTION,
  NOTIFICATION_TITLE,
  getInitials,
} from "creso-core/utils";
import { useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { Loader2 } from "lucide-react";
import { cn, TooltipContent, Tooltip, TooltipTrigger } from "web-design-system";
import { Avatar } from "web-core";
import { format, formatDistanceToNow, subDays } from "date-fns";
import { useNotifications } from "creso-core/hooks";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";

const NEGATIVE_EVENT_TERMS = ["fail", "reject", "cancel"];

type RecentActivityProps = {
  activities?: Notification[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  title?: string;
  showTitle?: boolean;
  days?: number;
  className?: string;
  listClassName?: string;
  showClientAvatar?: boolean;
};

export function OverviewRecentActivity({ clientId }: { clientId: number }) {
  const weekAgo = useMemo(
    () => format(subDays(new Date(), 7), "yyyy-MM-dd"),
    []
  );

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotifications({ clientId, minDate: weekAgo });

  const activities = useMemo(
    () => data?.pages.flatMap((page) => page.results),
    [data]
  );

  return (
    <RecentActivity
      activities={activities}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      days={7}
    />
  );
}

export function RecentActivity({
  activities,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  title = "Recent Activity",
  showTitle = true,
  days,
  className,
  listClassName,
  showClientAvatar = false,
}: RecentActivityProps) {
  const { ref: loaderRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!activities || !activities.length) return null;

  return (
    <div className={cn("flex flex-col max-h-85", className)}>
      {showTitle ? (
        <div className="flex items-center justify-between gap-4 py-4 bg-background">
          <span className="text-base font-semibold">{title}</span>
          {days != null ? (
            <span className="text-xs text-muted-foreground">
              Last {days} days
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          "flex flex-col flex-1 gap-2 overflow-auto",
          listClassName
        )}
      >
        {activities.map((activity) => (
          <ActivityItem
            activity={activity}
            showClientAvatar={showClientAvatar}
            key={activity.id}
          />
        ))}
        {hasNextPage ? (
          <div
            ref={loaderRef}
            className="h-10 w-full flex justify-center items-center shrink-0"
          >
            <Loader2 className="animate-spin size-4" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ActivityItem({
  activity,
  showClientAvatar = false,
}: {
  activity: Notification;
  showClientAvatar?: boolean;
}) {
  const isNegative = NEGATIVE_EVENT_TERMS.some((term) =>
    activity.event_type.toLocaleLowerCase().includes(term)
  );

  return (
    <LandingLink className="[&>div]:flex [&>div]:p-0" notification={activity}>
      <div
        className={cn(
          "bg-card w-full text-sm flex gap-3 items-center rounded-lg px-5 py-4 border-l-3",
          isNegative ? "border-destructive" : "border-constructive"
        )}
      >
        {showClientAvatar && activity.details.client ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Avatar
                  fallback={getInitials(activity.details.client.name)}
                  size="sm"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent portal>
              {activity.details.client.name}
              {activity.details.client.holding_pattern &&
              JOINT_CLIENT_HOLDING_PATTERNS.includes(
                activity.details.client.holding_pattern
              )
                ? ` (Joint)`
                : null}
            </TooltipContent>
          </Tooltip>
        ) : null}
        <span className="font-semibold">
          {NOTIFICATION_TITLE[activity.event_type]}
        </span>
        <span className="flex-1 truncate">
          {NOTIFICATION_DESCRIPTION[activity.event_type]?.(activity.details)}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatDistanceToNow(activity.created_at, { addSuffix: true })
            .replace("about", "")
            .replace("minutes", "mins")}
        </span>
      </div>
    </LandingLink>
  );
}
